import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { SearchXIcon } from 'lucide-react';
import { Link } from 'react-router';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Paginator from '@/components/ui/paginator';
import { Card } from '@/components/ui/card';
import { cn } from 'src/lib/utils';
import { EmptyBlock } from '@/components/ui/empty-block';

import { getAuditLogContent, useAuditLogs, type IAuditLogNode, type ILogType } from '../model';

import AuditLogsByCell from './AuditLogsByCell';
import TypeTooltip from './TypeTooltip';

const columnHelper = createColumnHelper<IAuditLogNode>();

const columns = [
  columnHelper.accessor('eventType', {
    id: 'activity',
    header: 'Activity',
    cell: (props) => {
      const { parentType, parentId, payload } = props.row.original;
      const auditContent = getAuditLogContent({
        eventType: props.getValue() as ILogType,
        payload,
        parentType,
        parentId,
      });

      if (!auditContent) return null;

      const { parts, icon: Icon } = auditContent;

      return (
        <div className="flex items-start gap-2">
          {Icon && (
            <TypeTooltip type={parentType === 'Escrow' ? payload.escrow_type : parentType}>
              <div className="mt-[2px] shrink-0">
                <Icon className="size-4.5" />
              </div>
            </TypeTooltip>
          )}
          <div className="text-foreground grow">
            <span className="text-sm font-medium">
              {parts.map(({ text, href }, i) =>
                href ? (
                  <Link key={i} to={href} className="underline">
                    {text}
                  </Link>
                ) : (
                  <span key={i}>{text}</span>
                ),
              )}
            </span>
          </div>
        </div>
      );
    },
  }),
  columnHelper.display({
    header: 'By',
    cell: ({ row }) => {
      const { source, payload, user, eventType } = row.original;
      return <AuditLogsByCell source={source} payload={payload} user={user} eventType={eventType} />;
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'When',
    cell: (props) => {
      const date = dayjs(props.getValue());
      const formatted = dayjs().diff(date, 'day') > 7 ? date.format('MMM D, YYYY') : date.fromNow();
      return <span className="text-sm text-foreground whitespace-nowrap">{formatted}</span>;
    },
  }),
];

const AuditLogsTable = () => {
  const { nodes, pagesCount, page, setPage, isLoading, startTransition } = useAuditLogs();

  const table = useReactTable({
    data: nodes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <Table isLoading={isLoading} className="min-w-167.5">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'bg-gray-50 text-slate-700 font-bold text-xs first:rounded-tl-md last:rounded-tr-md uppercase',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {nodes.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="group-last/row:first:rounded-bl-md group-last/row:last:rounded-br-md"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <EmptyBlock
                    title="No results found"
                    description="There were no results for your search. Try another filter."
                    icon={SearchXIcon}
                    withCard={false}
                    classes={{
                      title: 'text-base',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Paginator
        currentPage={page}
        totalPages={pagesCount}
        onPageChange={(p) => startTransition(() => setPage(p))}
        showPreviousNext
        hideOnSinglePage
        className="mb-4"
      />
    </div>
  );
};

export default AuditLogsTable;
